/**
 * Health Check Service
 * 
 * Provides functions to check API health status
 */
import axios from 'axios';
import { retryWithBackoff } from '@/shared/utils/retry';
import { getTranslatedErrorMessage } from '@/shared/utils/errorMessages';

// Get the API base URL - we need to hit /health endpoint on apimain
const getHealthUrl = () => {
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

    if (apiBaseUrl && apiBaseUrl.startsWith('http')) {
        // Remove trailing slash if present
        const baseUrl = apiBaseUrl.replace(/\/$/, '');
        return `${baseUrl}/health`;
    }

    // Fallback to relative path based on current origin
    if (typeof globalThis !== 'undefined' && globalThis.location) {
        return `${globalThis.location.origin}/apimain/health`;
    }

    return '/apimain/health';
};

/**
 * Check if the API is healthy
 * @param {number} timeout - Request timeout in milliseconds (default: 10000)
 * @returns {Promise<{isHealthy: boolean, data: object|null, error: string|null}>}
 */
export const checkHealth = async (timeout = 10000) => {
    try {
        const healthUrl = getHealthUrl();

        const response = await retryWithBackoff(() =>
            axios.get(healthUrl, {
                timeout,
                // Don't use the apiClient to avoid auth interceptors
                withCredentials: false
            })
        );

        // Check if response has the expected structure
        if (response.data && response.data.status === 'ok') {
            return {
                isHealthy: true,
                data: response.data,
                error: null
            };
        }

        // Response received but status is not 'ok'
        return {
            isHealthy: false,
            data: response.data,
            error: `Unexpected status: ${response.data?.status || 'unknown'}`
        };

    } catch (error) {
        // Network error, timeout, or server error
        let errorMessage = 'Unknown error';
        const status = error?.response?.status;

        if (status === 503) {
            errorMessage = getTranslatedErrorMessage('SERVICE_UNAVAILABLE') || 'Service temporarily unavailable. Please try again later.';
        } else if (error.code === 'ECONNABORTED') {
            errorMessage = 'Request timeout';
        } else if (error.response) {
            // Server responded with error status
            errorMessage = `Server error: ${error.response.status}`;
        } else if (error.request) {
            // Request was made but no response received
            errorMessage = 'No response from server';
        } else {
            errorMessage = error.message || 'Unknown error';
        }

        return {
            isHealthy: false,
            data: null,
            error: errorMessage
        };
    }
};

/**
 * Create a health check polling function
 * @param {function} onHealthy - Callback when health is restored
 * @param {function} onUnhealthy - Callback when health check fails
 * @param {number} interval - Polling interval in milliseconds (default: 30000)
 * @returns {{ start: function, stop: function }}
 */
export const createHealthPoller = (onHealthy, onUnhealthy, interval = 30000) => {
    let timerId = null;
    let isRunning = false;

    const poll = async () => {
        if (!isRunning) return;

        const result = await checkHealth();

        if (result.isHealthy) {
            if (onHealthy) onHealthy(result);
        } else {
            if (onUnhealthy) onUnhealthy(result);
        }
    };

    return {
        start: () => {
            if (isRunning) return;
            isRunning = true;
            poll(); // Immediate check
            timerId = setInterval(poll, interval);
        },
        stop: () => {
            isRunning = false;
            if (timerId) {
                clearInterval(timerId);
                timerId = null;
            }
        }
    };
};

export default {
    checkHealth,
    createHealthPoller
};
